/**
 * MeusProcessos Page (Simplified for Story 11.5)
 * Main page for partner users to view their assigned processos
 */
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { useProcessosList } from "../../hooks/useProcessosParceiro";
import { ProcessoFilterParams } from "../../types/processo-parceiro";
import ProcessoListCard from "../../components/processos/ProcessoListCard";
import ProcessoFilterPanel from "../../components/processos/ProcessoFilterPanel";
import ProcessoDetailsModal from "../../components/processos/ProcessoDetailsModal";

const MeusProcessos: React.FC = () => {
  const [filters, setFilters] = useState<ProcessoFilterParams>({});
  const [page, setPage] = useState(1);
  const [selectedProcessoId, setSelectedProcessoId] = useState<string | null>(null);
  const pageSize = 12;

  const { data, isLoading, isError, error } = useProcessosList(filters, page, pageSize);

  const handleFilterChange = (newFilters: ProcessoFilterParams) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleProcessoClick = (processoId: string) => {
    setSelectedProcessoId(processoId);
  };

  const stats = React.useMemo(() => {
    if (!data) return null;
    const total = data.total;
    const ativos = data.items.filter(
      (p) => p.status !== "concluido" && p.status !== "cancelado"
    ).length;
    const vencidos = data.items.filter((p) => p.is_prazo_vencido).length;
    const concluidos = data.items.filter((p) => p.status === "concluido").length;
    return { total, ativos, vencidos, concluidos };
  }, [data]);

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Meus Processos</h1>
        <p className="text-muted-foreground">
          Acompanhe os processos atribuídos ao seu substabelecido
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.ativos}</p>
                <p className="text-sm text-muted-foreground">Em Andamento</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{stats.vencidos}</p>
                <p className="text-sm text-muted-foreground">Vencidos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.concluidos}</p>
                <p className="text-sm text-muted-foreground">Concluídos</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Panel */}
      <ProcessoFilterPanel onFilterChange={handleFilterChange} initialFilters={filters} />

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>{error?.message || "Erro ao carregar processos"}</AlertDescription>
        </Alert>
      )}

      {/* Processos Grid */}
      {data && data.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.items.map((processo) => (
            <ProcessoListCard
              key={processo.id}
              processo={processo}
              onClick={() => handleProcessoClick(processo.id)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {data && data.items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <FileText className="w-16 h-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum processo encontrado</h3>
          <p className="text-muted-foreground">
            {Object.keys(filters).length > 0
              ? "Tente ajustar os filtros"
              : "Não há processos atribuídos no momento"}
          </p>
        </div>
      )}

      {/* Details Modal */}
      {selectedProcessoId && (
        <ProcessoDetailsModal
          processoId={selectedProcessoId}
          isOpen={!!selectedProcessoId}
          onClose={() => setSelectedProcessoId(null)}
        />
      )}
    </div>
  );
};

export default MeusProcessos;
